import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const TutorSessionHistoryPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        setLoading(true);
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          navigate('/signin');
          return;
        }

        const user = JSON.parse(userStr);
        const tutorId = user.prospectId;
        if (!tutorId) {
          setError('Tutor profile not found.');
          return;
        }

        const data = await apiRequest(
          `/api/v1/bookings/tutor/${tutorId}/sessions?status=completed&limit=50`,
        );
        const rows = Array.isArray(data) ? data : data.data || data.sessions || [];
        setSessions(rows);
      } catch (err) {
        console.error(err);
        setError('Failed to load session history.');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [navigate]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-deep-navy sm:text-3xl">Session History</h1>
          <p className="mt-2 text-gray-600">Completed sessions with your learners.</p>
        </div>
        <Link to="/tutor-dashboard" className="text-sm text-gray-600 hover:text-brand-dark-blue">
          ← Back to Dashboard
        </Link>
      </div>

      {loading ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-4 border-brand-medium-blue" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      ) : sessions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-gray-600">
          No completed sessions yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-[42rem] divide-y divide-gray-200 sm:min-w-full">
            <thead className="bg-brand-light-blue/40">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-brand-deep-navy">Date / Time</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-brand-deep-navy">Session ID</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-brand-deep-navy">Learner</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-brand-deep-navy">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-700">{formatDateTime(session.startTime)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{session.referenceNumber || session.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {session.student?.firstName
                      ? session.student.firstName
                      : `Learner #${session.studentProfileId}`}
                  </td>
                  <td className="px-6 py-4 text-sm capitalize text-brand-medium-blue">{session.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
